import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import PropTypes from 'prop-types'

import cn from 'classnames'
import styles from './QRCodeReceipt.module.scss'

import { APPLICATION_STRINGS } from '../../strings'

import ReceiptModal, { RECEIPT_TYPES } from './ReceiptModal'
import ConfirmModalV2 from '../ConfirmModalV2'

import { getPrintingEnabled } from '../../selectors/printer'
import {
  getKitchenPrintingEnabled,
  getTextReceiptsEnabled,
  getEmailReceiptsEnabled,
  getPosQrReceiptsEnabled,
} from '../../selectors/menus'
import { getTipOnCardReader } from '../../selectors/peripheral'

import { createOrderParams } from '../../utils/orderUtils'
import { centsToDollars } from '../../utils/formatters'
import { getNetworkAvailableValue } from '../../utils/networkConnected'
import usePrinter from '../../VNPrinters/hooks/usePrinter'
import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'
import { Button } from '@material-ui/core'
import { generateReceiptQrUrl } from './ReceiptUtils'
import QRCode from '../QRCode'
import { setMobileQRReceiptDialog } from '../../VNDialogs/ActionCreators'
import { ORDER_STATES } from '../../utils/orderStates'
import { getQrReceiptsUrl } from '../../selectors/menus'

const QRCodeReceipt = ({
  onDone,
  order,
  className,
  paymentId,
  hasBalanceDueInCents,
  balanceDueDisplay,
  isSplitTender,
  isNewOrder,
  goToDeviceSummary,
}) => {
  const dispatch = useDispatch()
  const printingEnabled = useSelector((state) => getPrintingEnabled(state))
  const cloudPrintingEnabled = useSelector((state) => getKitchenPrintingEnabled(state))
  const tipOnCardReader = useSelector((state) => getTipOnCardReader(state))
  const enableTextReceipts = useSelector((state) => getTextReceiptsEnabled(state)) && !tipOnCardReader
  const enableEmailReceipts = useSelector((state) => getEmailReceiptsEnabled(state)) && !tipOnCardReader
  const enablePosQrReceipts = useSelector((state) => getPosQrReceiptsEnabled(state))
  const owUrl = useSelector(getQrReceiptsUrl)
  const receiptQrUrl = generateReceiptQrUrl(order?.uuid, owUrl)
  const totalInCents = order?.amountInCents

  const networkAvailable = getNetworkAvailableValue()
  const urlParams = new URLSearchParams(window.location.search)
  const fromOrderDetails = urlParams.get('origin') === 'orderDetails'

  const [showSendEmailReceiptModal, setShowSendEmailReceiptModal] = useState(false)
  const [showSendTextReceiptModal, setShowSendTextReceiptModal] = useState(false)
  const [showReceiptErrorDialog, setShowReceiptErrorDialog] = useState(false)
  const [showKitchenPrintErrorDialog, setShowKitchenPrintErrorDialog] = useState(
    fromOrderDetails ? false : !networkAvailable && cloudPrintingEnabled && !order.isTab,
  )
  const { printReceipt } = usePrinter(order?.uuid)

  const classNames = cn(styles.qrCodeReceipt, className)
  const isMobileViewPort = useGetIsMobileScreen()

  const payments = createOrderParams(order)?.payments ?? []
  const wasCreatedInStadium = order?.wasCreatedInStadium

  const isMobilePay = payments[0]?.paymentType === 'mobile_pay'

  const showEmailAndTextButtons = useMemo(() => {
    return !isMobilePay && !hasBalanceDueInCents && networkAvailable
  }, [isMobilePay, hasBalanceDueInCents, networkAvailable])

  const emailButton = useMemo(
    () => (isMobileViewPort ? APPLICATION_STRINGS.EMAIL_BUTTON_SHORT_TEXT : APPLICATION_STRINGS.EMAIL_BUTTON_LONG_TEXT),
    [isMobileViewPort],
  )
  const textButton = useMemo(
    () => (isMobileViewPort ? APPLICATION_STRINGS.TEXT_BUTTON_SHORT_TEXT : APPLICATION_STRINGS.TEXT_BUTTON_LONG_TEXT),
    [isMobileViewPort],
  )
  const printButton = useMemo(
    () => (isMobileViewPort ? APPLICATION_STRINGS.PRINT_BUTTON_SHORT_TEXT : APPLICATION_STRINGS.PRINT_BUTTON_LONG_TEXT),
    [isMobileViewPort],
  )

  let nextText = APPLICATION_STRINGS.NEXT_BUTTON_NO_BALANCE

  if (hasBalanceDueInCents) {
    nextText = APPLICATION_STRINGS.NEXT_BUTTON_BALANCE
  }

  const FooterElement = () => {
    if (order.remainingTicketBalance !== undefined) {
      return (
        <div className={styles.balanceDue}> {`Remaining Ticket Balance: ${centsToDollars(order.remainingTicketBalance)}`}</div>
      )
    }

    if (hasBalanceDueInCents) {
      return <div className={styles.balanceDue}> {`Balance Due: ${balanceDueDisplay}`}</div>
    }

    return null
  }

  const handleSendTextClick = () => {
    if (!wasCreatedInStadium) {
      setShowReceiptErrorDialog(true)
      return
    }

    setShowSendTextReceiptModal(true)
  }

  const handleSendEmailClick = () => {
    if (!wasCreatedInStadium) {
      setShowReceiptErrorDialog(true)
      return
    }

    setShowSendEmailReceiptModal(true)
  }

  const handleQrClick = () => {
    dispatch(
      setMobileQRReceiptDialog({
        open: true,
        qrCode: receiptQrUrl,
        total: totalInCents,
        onBack: () => dispatch(setMobileQRReceiptDialog({ open: false })),
        onClose: () => {
          dispatch(setMobileQRReceiptDialog({ open: false }))
          onDone()
        },
      }),
    )
  }

  const ReceiptModalComponent = ({ receiptType, allowMultiple }) => {
    const onModalClose = () => {
      setShowSendEmailReceiptModal(false)
      setShowSendTextReceiptModal(false)
    }

    return (
      <ReceiptModal
        onClose={() => onModalClose()}
        order={order}
        receiptType={receiptType}
        allowMultiple={allowMultiple}
        paymentId={paymentId}
      />
    )
  }

  return (
    <div className={classNames}>
      {showReceiptErrorDialog && (
        <ConfirmModalV2
          onButtonOneClick={() => setShowReceiptErrorDialog(false)}
          onButtonTwoClick={() => goToDeviceSummary()}
          headerText={APPLICATION_STRINGS.SEND_RECEIPT_ERROR_TITLE}
          subtext={APPLICATION_STRINGS.ORDER_MUST_BE_SYNCED_BEFORE_SENDING_RECEIPT}
          buttonOneText={APPLICATION_STRINGS.OK_BUTTON_TEXT}
          buttonTwoText={'SYNC NOW'}
        />
      )}
      {showKitchenPrintErrorDialog && (
        <ConfirmModalV2
          onButtonOneClick={() => setShowKitchenPrintErrorDialog(false)}
          onButtonTwoClick={() => setShowKitchenPrintErrorDialog(false)}
          headerText={APPLICATION_STRINGS.KITCHEN_PRINTER_ERROR_TITLE}
          subtext={APPLICATION_STRINGS.OFFLINE_KITCHEN_PRINTER_ERROR}
          buttonOneText={APPLICATION_STRINGS.OK_BUTTON_TEXT}
          buttonTwoText={APPLICATION_STRINGS.OK_BUTTON_TEXT}
          singleButtonOnly={true}
        />
      )}
      <div className={cn([styles.footer, (hasBalanceDueInCents || order.remainingTicketBalance) && styles.flexColumn])}>
        <div className={styles.mobileButtons}>
          {receiptQrUrl && !isMobileViewPort && order.state !== ORDER_STATES.REFUNDED && enablePosQrReceipts && !hasBalanceDueInCents && (
            <div className={styles.qrContainer}>
              <div className={styles.qrTitle}>{APPLICATION_STRINGS.SCAN_DIGITAL_RECEIPT}</div>
              <div className={styles.qrcode}>
                <QRCode size={150} data={receiptQrUrl} />
              </div>
            </div>
          )}
          {showEmailAndTextButtons && (
            <>
              {enableEmailReceipts && (
                <Button
                  title={APPLICATION_STRINGS.EMAIL_BUTTON_LONG_TEXT}
                  className={styles.receiptButton}
                  onClick={handleSendEmailClick}
                >
                  {emailButton}
                </Button>
              )}
              {enableTextReceipts && (
                <Button
                  title={APPLICATION_STRINGS.TEXT_BUTTON_LONG_TEXT}
                  className={styles.receiptButton}
                  onClick={handleSendTextClick}
                >
                  {textButton}
                </Button>
              )}
            </>
          )}
          {receiptQrUrl && isMobileViewPort && order.state !== ORDER_STATES.REFUNDED && enablePosQrReceipts && !hasBalanceDueInCents && (
            <Button title={APPLICATION_STRINGS.QR_BUTTON_TEXT} className={styles.receiptButton} onClick={handleQrClick}>
              {APPLICATION_STRINGS.QR_BUTTON_TEXT}
            </Button>
          )}
          {printingEnabled && !hasBalanceDueInCents && (
            <Button
              title={APPLICATION_STRINGS.PRINT_BUTTON_LONG_TEXT}
              className={styles.receiptButton}
              onClick={() => printReceipt(isSplitTender, isNewOrder)}
            >
              {printButton}
            </Button>
          )}
          {(!isMobileViewPort || hasBalanceDueInCents) && (
            <Button className={styles.doneButton} onClick={onDone}>
              {nextText}
            </Button>
          )}
        </div>
        <FooterElement />
        {(showSendEmailReceiptModal || showSendTextReceiptModal) && (
          <ReceiptModalComponent
            receiptType={showSendEmailReceiptModal ? RECEIPT_TYPES.EMAIL : RECEIPT_TYPES.TEXT}
            allowMultiple={showSendEmailReceiptModal}
          />
        )}
      </div>
    </div>
  )
}

QRCodeReceipt.defaultProps = {
  title: '',
  menu: {},
  order: {},
  onDone: () => {},
  goToDeviceSummary: () => {},
  cloudPrintingEnabled: false,
  isRichCheckout: false,
}

QRCodeReceipt.propTypes = {
  title: PropTypes.string,
  menu: PropTypes.object,
  order: PropTypes.object,
  onDone: PropTypes.func,
  goToDeviceSummary: PropTypes.func,
  cloudPrintingEnabled: PropTypes.bool,
  isRichCheckout: PropTypes.bool,
}

export default QRCodeReceipt
